import React, { useEffect, useRef } from 'react';
import { useFramework } from '@/framework';
import { type TextLayer } from '@/layers/text-layer';
import {
    BaseLayerViewType,
    ITextLayerProps,
    LayerType,
    PluginType,
    type IExportPlugin,
    type ILayerProps,
} from '@/version-types';

const ShapeTextRefresher: React.FC = React.memo(() => {
    const framework = useFramework();

    const layerRef = useRef<ILayerProps | null>(null);
    const prevLayerRef = useRef<ILayerProps | null>(null);

    useEffect(() => {
        const handler = framework.events.onActiveLayerDataUpdate((activeLayer, pickLayer) => {
            const targetLayer = pickLayer || activeLayer;
            if (!targetLayer || targetLayer.layerType !== LayerType.TEXT_LAYER) {
                layerRef.current = null;
                return;
            }

            const layerData = targetLayer.data as ITextLayerProps;
            const { shapeExtension } = layerData;
            layerRef.current = shapeExtension ? targetLayer : null;
        });

        return () => {
            framework.events.off(handler);
        };
    }, []);

    useEffect(() => {
        const tryGetTextLayer = () => {
            const textLayer = framework.getInstance<TextLayer>('TextLayer');
            if (!textLayer) {
                console.error('TextLayer is not found');
                return null;
            }
            return textLayer;
        };

        const handler = framework.events.onBaseOperate((layerId, operateType, args) => {
            if (!layerRef.current) return;

            const { isEnd, isStart } = args;

            if (layerRef.current.id !== layerId) return;

            if (operateType === BaseLayerViewType.SCALE) {
                if (isStart) {
                    const layerOnStart = framework.getLayer(layerId, true);
                    prevLayerRef.current = layerOnStart;
                }

                if (isEnd) {
                    const realtimeLayer = framework.getLayer(layerId, true);
                    if (!realtimeLayer) return;

                    tryGetTextLayer()
                        ?.refreshLayerScreenshotURL(realtimeLayer)
                        .then((nextLayer) => {
                            if (nextLayer) {
                                framework.stage.addOrUpdate(nextLayer);
                                framework.operate.setActiveLayer(nextLayer.id);
                                if (prevLayerRef.current) {
                                    framework.history.write(prevLayerRef.current, nextLayer);
                                }
                            }
                            prevLayerRef.current = null;
                        });
                }
            }
        });

        return () => {
            framework.events.off(handler);
        };
    }, []);

    return null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.SYSTEM,
    sort: 1,
    name: 'shape-text-refresher',
    render() {
        return <ShapeTextRefresher />;
    },
};

export default exportPlugin;
